import { useEffect, useState } from 'react';
import { Alert, App, Form, Input, Modal, Radio, Select, Typography } from 'antd';
import type {
  Customer,
  CustomerDocumentType,
  CustomerStatus,
  FleetDriver,
  FleetDriverCreateRequest,
  FleetDriverUpdateRequest,
} from '@goelectric/shared-types';
import { CUSTOMER_STATUS_META } from '@goelectric/ui-components';
import { customerApi } from '@/features/clientes/api/customerApi';
import { fleetApi } from '../api/fleetApi';
import { describeFleetApiError } from '../utils/fleetErrors';
import { FLEET_DOCUMENT_TYPE_LABELS } from '../utils/format';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

const DOCUMENT_TYPE_OPTIONS = (['CC', 'CE', 'NIT', 'PPT'] as const).map((value) => ({
  value,
  label: FLEET_DOCUMENT_TYPE_LABELS[value],
}));

const DRIVER_STATUS_OPTIONS = (['ACTIVE', 'SUSPENDED'] as const).map((value) => ({
  value,
  label: CUSTOMER_STATUS_META[value].label,
}));

export interface DriverFormState {
  customerId?: string;
  fullName?: string;
  phone?: string;
  email?: string;
  documentType?: CustomerDocumentType;
  documentNumber?: string;
  status?: CustomerStatus;
}

interface DriverOption {
  value: string;
  label: string;
}

interface InlineDriverBody {
  fullName: string;
  phone: string;
  email?: string;
  documentType?: CustomerDocumentType;
  documentNumber?: string;
  status?: CustomerStatus;
}

/** Compone el cuerpo del alta inline (`fullName` + `phone` + opcionales). */
export function buildInlineDriverBody(values: DriverFormState): FleetDriverCreateRequest {
  const body: InlineDriverBody = {
    fullName: values.fullName?.trim() ?? '',
    phone: values.phone?.trim() ?? '',
  };
  const email = values.email?.trim();
  if (email) body.email = email;
  if (values.documentType) body.documentType = values.documentType;
  const documentNumber = values.documentNumber?.trim();
  if (documentNumber) body.documentNumber = documentNumber;
  if (values.status) body.status = values.status;
  return body;
}

/** Compone el cuerpo del PATCH del conductor (sin `customerId`: no cambia la vinculación). */
export function buildDriverUpdateBody(values: DriverFormState): FleetDriverUpdateRequest {
  const body: FleetDriverUpdateRequest = {};
  const fullName = values.fullName?.trim();
  if (fullName) body.fullName = fullName;
  const phone = values.phone?.trim();
  if (phone) body.phone = phone;
  const email = values.email?.trim();
  if (email) body.email = email;
  if (values.documentType) body.documentType = values.documentType;
  const documentNumber = values.documentNumber?.trim();
  if (documentNumber) body.documentNumber = documentNumber;
  if (values.status) body.status = values.status;
  return body;
}

export interface DriverFormModalProps {
  open: boolean;
  fleetId: string;
  /** Conductor a editar; `null` para vincular o crear uno nuevo. */
  driver: FleetDriver | null;
  onClose: () => void;
  /** Refresca la tabla de conductores y la flota tras guardar. */
  onSaved: () => void;
}

type DriverMode = 'link' | 'create';

/**
 * Alta/edición de conductor de una flota (US-2.3, ADR 0047).
 *
 * En el alta se ofrecen las dos vías del contrato: **vincular** un cliente CONDUCTOR
 * existente (búsqueda server-side con `customerApi.list({tipoCliente:'CONDUCTOR'})`) o
 * **crear** el cliente inline con `fullName` + `phone`. En edición no se puede cambiar la
 * vinculación (`customerId`); el PATCH actualiza la ficha.
 */
export function DriverFormModal({
  open,
  fleetId,
  driver,
  onClose,
  onSaved,
}: DriverFormModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<DriverFormState>();
  const [mode, setMode] = useState<DriverMode>('link');
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<DriverOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = driver !== null;

  // Al abrir: limpia el formulario y precarga la ficha en edición.
  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    setSearch('');
    setMode('link');
    form.resetFields();
    if (driver) {
      form.setFieldsValue({
        fullName: driver.fullName,
        phone: driver.phone,
        email: driver.email,
        documentType: driver.documentType,
        documentNumber: driver.documentNumber,
        status: driver.status,
      });
    } else {
      form.setFieldsValue({ status: 'ACTIVE' });
    }
  }, [open, driver, form]);

  // Búsqueda de conductores existentes (solo en el modo "vincular").
  useEffect(() => {
    if (!open || driver || mode !== 'link') {
      return;
    }

    let cancelled = false;
    setSearching(true);
    const handle = window.setTimeout(() => {
      customerApi
        .list({
          q: search.trim() || undefined,
          tipoCliente: 'CONDUCTOR',
          limit: SEARCH_LIMIT,
        })
        .then((page) => {
          if (cancelled) return;
          setOptions(
            page.data
              .filter((customer: Customer) => customer.tipoCliente !== 'FLOTA')
              .map((customer) => ({
                value: customer.id,
                label: `${customer.fullName} · ${customer.phone}`,
              })),
          );
        })
        .catch(() => {
          // La búsqueda es auxiliar: si falla, se degrada a "sin opciones".
          if (!cancelled) setOptions([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, driver, mode, search]);

  const handleFinish = async (values: DriverFormState) => {
    setSubmitting(true);
    setError(null);
    try {
      if (driver) {
        await fleetApi.updateDriver(fleetId, driver.customerId, buildDriverUpdateBody(values));
        message.success('Conductor actualizado.');
      } else if (mode === 'link') {
        if (!values.customerId) {
          return;
        }
        await fleetApi.linkDriver(fleetId, { customerId: values.customerId });
        message.success('Conductor vinculado a la flota.');
      } else {
        await fleetApi.linkDriver(fleetId, buildInlineDriverBody(values));
        message.success('Conductor creado y vinculado a la flota.');
      }
      onSaved();
      onClose();
    } catch (caught) {
      const { message: detail, field } = describeFleetApiError(
        caught,
        driver ? 'No se pudo actualizar el conductor.' : 'No se pudo vincular el conductor.',
      );
      setError(detail);
      if (field) {
        form.setFields([{ name: field as keyof DriverFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Editar conductor' : 'Vincular conductor'}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? 'Guardar' : 'Vincular'}
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={620}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo guardar el conductor"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {isEdit && driver ? (
        <Typography.Paragraph type="secondary">
          Conductor vinculado: <Typography.Text strong>{driver.fullName}</Typography.Text> (
          <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{driver.customerId}</span>)
        </Typography.Paragraph>
      ) : (
        <Radio.Group
          value={mode}
          onChange={(event) => setMode(event.target.value as DriverMode)}
          aria-label="Modo de vinculación"
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="link">Vincular cliente existente</Radio.Button>
          <Radio.Button value="create">Crear conductor nuevo</Radio.Button>
        </Radio.Group>
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish} autoComplete="off">
        {!isEdit && mode === 'link' && (
          <Form.Item
            label="Conductor existente"
            name="customerId"
            rules={[{ required: true, message: 'Selecciona el conductor a vincular.' }]}
          >
            <Select<string>
              showSearch
              filterOption={false}
              onSearch={setSearch}
              placeholder="Buscar por nombre, teléfono o documento"
              aria-label="Buscar conductor existente"
              loading={searching}
              options={options}
              notFoundContent={searching ? 'Buscando…' : 'Sin conductores disponibles'}
            />
          </Form.Item>
        )}

        {(isEdit || mode === 'create') && (
          <>
            <Form.Item
              label="Nombre completo"
              name="fullName"
              rules={[{ required: true, message: 'Ingresa el nombre del conductor.' }]}
            >
              <Input placeholder="Ej. Juan Pérez" />
            </Form.Item>

            <Form.Item
              label="Teléfono"
              name="phone"
              rules={[{ required: true, message: 'Ingresa el teléfono del conductor.' }]}
            >
              <Input placeholder="Ej. +573001234567" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[{ type: 'email', message: 'Ingresa un email válido.' }]}
            >
              <Input placeholder="Ej. juan.perez@empresa.co" />
            </Form.Item>

            <Form.Item label="Tipo de documento" name="documentType">
              <Select<CustomerDocumentType>
                allowClear
                placeholder="Selecciona el tipo de documento"
                options={DOCUMENT_TYPE_OPTIONS}
              />
            </Form.Item>

            <Form.Item label="Número de documento" name="documentNumber">
              <Input placeholder="Ej. 1020304050" />
            </Form.Item>

            {isEdit && (
              <Form.Item label="Estado" name="status">
                <Select<CustomerStatus> options={DRIVER_STATUS_OPTIONS} />
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
}
